import { useCallback, useMemo } from 'react';
import { ApolloQueryResult, gql, useQuery } from '@apollo/client';
import { last } from 'lodash';
import {
  Query,
  QueryScheduleAttendeeCandidatesArgs,
  ScheduleAttendeeCandidatesUnion,
  ScheduleAttendeeCandidatesWhereInput,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { getNodesFromConnection } from 'src/utils/graphql/get-nodes-from-connection';

export const GET_ATTENDEE_CANDIDATES = gql`
  query GetAttendeeCandidates(
    $institutionId: Int!
    $where: ScheduleAttendeeCandidatesWhereInput
    $after: String
    $first: Int
  ) {
    scheduleAttendeeCandidates(
      institutionId: $institutionId
      where: $where
      after: $after
      first: $first
    ) {
      edges {
        cursor
        node {
          __typename
          ... on Contact {
            id
            label
            stage {
              name
              slug
            }
          }
          ... on User {
            id
            fullName
            role {
              id
            }
            avatar {
              url(transform: { width: 180, height: 180 })
              color
              initials
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export type AttendeeCandidatesRefetchType = (
  variables?: Partial<Partial<QueryScheduleAttendeeCandidatesArgs>> | undefined,
) => Promise<ApolloQueryResult<Pick<Query, 'scheduleAttendeeCandidates'>>>;

type UseAttendeeCandidatesReturn = {
  loading: boolean;
  candidates: ScheduleAttendeeCandidatesUnion[];
  refetch: AttendeeCandidatesRefetchType;
  loadMore: () => Promise<void>;
  hasNextPage?: boolean;
};

export const useAttendeeCandidates = (
  where?: ScheduleAttendeeCandidatesWhereInput,
): UseAttendeeCandidatesReturn => {
  const currentInstitution = useCurrentInstitution();
  const { data, loading, refetch, fetchMore } = useQuery<
    Pick<Query, 'scheduleAttendeeCandidates'>,
    Partial<QueryScheduleAttendeeCandidatesArgs>
  >(GET_ATTENDEE_CANDIDATES, {
    variables: {
      institutionId: currentInstitution.id,
      where,
    },
  });
  const hasNextPage = useMemo(() => {
    return data?.scheduleAttendeeCandidates?.pageInfo.hasNextPage ?? false;
  }, [data?.scheduleAttendeeCandidates?.pageInfo.hasNextPage]);

  const loadMore = useCallback(async () => {
    const lastCursor = last(data?.scheduleAttendeeCandidates.edges)?.cursor;

    if (!lastCursor || !hasNextPage) {
      return;
    }

    await fetchMore({
      variables: {
        after: lastCursor,
        first: 100,
        where,
      },
    });
  }, [data?.scheduleAttendeeCandidates.edges, fetchMore, hasNextPage, where]);

  return {
    candidates: getNodesFromConnection(data?.scheduleAttendeeCandidates),
    loading,
    refetch,
    loadMore,
    hasNextPage,
  };
};
